import { ChangeDetectionStrategy, Component, inject } from '@angular/core';

import { AlertController } from '@ionic/angular';
import { select, Store } from '@ngrx/store';

import { Observable } from 'rxjs';

import * as fromPropertyProfileActions from '../../store/actions/property-profile.actions';
import * as fromPushNotificationsActions from '../../store/actions/push-notifications.actions';
import * as fromSettingActions from '../../store/actions/setting.actions';
import * as fromUserActions from '../../store/actions/user.actions';
import * as fromWalkingFarmActions from '../../store/actions/walking-farm.actions';
import { State as DeviceState } from '../../store/reducers/device.reducer';
import { State as MapState } from '../../store/reducers/map.reducer';
import { State as MonetizationState } from '../../store/reducers/monetization.reducer';
import { State as OfficeState } from '../../store/reducers/office.reducer';
import { State as OrganizationState } from '../../store/reducers/organization.reducer';
import { State as PushNotificationsState } from '../../store/reducers/push-notifications.reducer';
import { State as SettingState } from '../../store/reducers/setting.reducer';
import { State as TitleRepState } from '../../store/reducers/title-rep.reducer';
import * as fromDataProviderSelectors from '../../store/selectors/data-provider.selectors';
import * as fromDeviceSelectors from '../../store/selectors/device.selectors';
import * as fromMapSelectors from '../../store/selectors/map.selectors';
import * as fromMonetizationSelectors from '../../store/selectors/monetization.selectors';
import * as fromOfficeSelectors from '../../store/selectors/office.selectors';
import * as fromOrganizationSelectors from '../../store/selectors/organization.selectors';
import * as fromPropertyProfileSelectors from '../../store/selectors/property-profile.selectors';
import * as fromPushNotificationsSelectors from '../../store/selectors/push-notifications.selectors';
import * as fromSettingSelectors from '../../store/selectors/setting.selectors';
import * as fromTitleRepSelectors from '../../store/selectors/title-rep.selectors';
import * as fromWalkingFarmSelectors from '../../store/selectors/walking-farm.selectors';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'red-debug',
  standalone: false,
  templateUrl: './debug.page.html'
})
export class DebugPageComponent {
  private store = inject(Store);
  private alertController = inject(AlertController);

  device$: Observable<DeviceState>;
  setting$: Observable<SettingState>;
  organization$: Observable<OrganizationState>;
  pushNotifications$: Observable<PushNotificationsState>;
  office$: Observable<OfficeState>;
  titleRep$: Observable<TitleRepState>;
  map$: Observable<MapState>;
  monetization$: Observable<MonetizationState>;
  providerId$: Observable<string | null>;
  providerUsername$: Observable<string | null>;
  profileKey$: Observable<string | null>;
  walkingFarmKey$: Observable<string | null>;

  constructor() {
    this.device$ = this.store.pipe(select(fromDeviceSelectors.selectDeviceFeature));
    this.setting$ = this.store.pipe(select(fromSettingSelectors.selectSettingFeature));
    this.organization$ = this.store.pipe(select(fromOrganizationSelectors.selectOrganizationFeature));
    this.office$ = this.store.pipe(select(fromOfficeSelectors.selectOfficeFeature));
    this.titleRep$ = this.store.pipe(select(fromTitleRepSelectors.selectTitleRepFeature));
    this.map$ = this.store.pipe(select(fromMapSelectors.selectMapFeature));
    this.monetization$ = this.store.pipe(select(fromMonetizationSelectors.selectMonetizationFeature));
    this.pushNotifications$ = this.store.pipe(select(fromPushNotificationsSelectors.selectPushNotificationsFeature));
    this.providerId$ = this.store.pipe(select(fromDataProviderSelectors.selectId));
    this.providerUsername$ = this.store.pipe(select(fromDataProviderSelectors.selectUsername));
    this.profileKey$ = this.store.pipe(select(fromPropertyProfileSelectors.selectKey));
    this.walkingFarmKey$ = this.store.pipe(select(fromWalkingFarmSelectors.selectKey));
  }

  async resetProfileKey() {
    const alert = await this.alertController.create({
      buttons: [
        {
          role: 'cancel',
          text: 'Cancel'
        },
        {
          role: 'destructive',
          text: 'Reset'
        }
      ],
      header: 'Reset Profile Key',
      message: 'Are you sure? The next property profile call will renew the key.'
    });

    await alert.present();

    const { role } = await alert.onDidDismiss();
    if (role === 'destructive') {
      this.store.dispatch(fromPropertyProfileActions.resetKey());
    }
  }

  async resetWalkingFarmKey() {
    const alert = await this.alertController.create({
      buttons: [
        {
          role: 'cancel',
          text: 'Cancel'
        },
        {
          role: 'destructive',
          text: 'Reset'
        }
      ],
      header: 'Reset Walking Farm Key',
      message: 'Are you sure? The next Walking Farm call will renew the key.'
    });

    await alert.present();

    const { role } = await alert.onDidDismiss();
    if (role === 'destructive') {
      this.store.dispatch(fromWalkingFarmActions.resetKey());
    }
  }

  sendTestPushNotification(type?: string) {
    this.store.dispatch(
      fromPushNotificationsActions.sendTestNotification({
        payload: {
          type
        }
      })
    );
  }

  syncConfiguration() {
    this.store.dispatch(fromSettingActions.refreshReferralCode());
  }

  syncUser() {
    this.store.dispatch(fromUserActions.reloadMobileUser());
  }
}
